import { forwardRef, ReactNode, useState } from 'react';

import LayoutDrawer from 'approot/shared/layout/layout-drawer';
import IconArrowLeft from 'approot/shared/icons/icon-arrow-left';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { useActivityBuilderContext } from './activity-builder.context';
import { useKeyBoardTrap } from 'approot/shared/ally/keyboard.hooks';
import * as Styled from './activity-builder-drawer.style';
import { ACTIVITY_BUILDER_DRAWER_ID } from './activity-builder.constants';
import { SecondaryButton } from 'approot/shared/styles/buttons/buttons';
import { formatToLowerKebabCase } from 'approot/shared/logic/format-to-lower-kebab-case';
import IconClose from 'approot/shared/icons/icon-close';

const ActivityBuilderDrawer = () => {
  const { isDrawerActive, closeDrawer } = useActivityBuilderContext();

  return (
    <Styled.ActivityBuilderDrawer $isActive={isDrawerActive}>
      <LayoutDrawer
        isSmall
        isActive={isDrawerActive}
        noScrollOnContainer
        captureKeypress={false}
        onClose={closeDrawer}
      >
        <Styled.ActivityBuilderDrawerIdWrapper
          id={ACTIVITY_BUILDER_DRAWER_ID}
        />
      </LayoutDrawer>
    </Styled.ActivityBuilderDrawer>
  );
};

export default ActivityBuilderDrawer;

type ActivityBuilderDrawerHeaderProps = {
  title?: ReactNode;
  isBack?: boolean;
  headerButton?: ReactNode;
  hasBorderBottom?: boolean;
  onClose: () => void;
};

export const ActivityBuilderDrawerHeader = ({
  title,
  isBack,
  headerButton,
  hasBorderBottom = true,
  onClose,
}: ActivityBuilderDrawerHeaderProps) => {
  return (
    <Styled.ActivityBuilderDrawerHeader
      $hasHeaderButton={!!headerButton}
      $hasBorderBottom={hasBorderBottom}
    >
      <Styled.ActivityBuilderDrawerClose
        type="button"
        aria-label={isBack ? 'Go back' : `Close ${title} view`}
        onClick={onClose}
        data-selector={isBack ? 'icon-go-back' : 'icon-close'}
      >
        {isBack ? (
          <IconArrowLeft width={16} height={16} />
        ) : (
          <IconClose width={16} height={16} />
        )}
      </Styled.ActivityBuilderDrawerClose>
      <Styled.ActivityBuilderDrawerTitle data-selector="lb-drawer-title">
        {title}
      </Styled.ActivityBuilderDrawerTitle>
      {headerButton}
    </Styled.ActivityBuilderDrawerHeader>
  );
};

export const ActivityBuilderDrawerContent = forwardRef(
  (
    {
      children,
      id,
      padding,
    }: {
      children: ReactNode;
      id?: string;
      padding?: number;
    },
    ref
  ) => {
    return (
      <Styled.ActivityBuilderDrawerContent
        id={id}
        ref={ref as any}
        $padding={padding}
      >
        {children}
      </Styled.ActivityBuilderDrawerContent>
    );
  }
);

export const ActivityBuilderDrawerActions = ({
  children,
}: {
  children: ReactNode;
}) => (
  <Styled.ActivityBuilderDrawerActions>
    {children}
  </Styled.ActivityBuilderDrawerActions>
);

export const ActivityBuilderDrawerWrapper = ({
  children,
  captureKeypresses = false,
  onCloseByEscape,
}: {
  children: ReactNode;
  captureKeypresses?: boolean;
  onCloseByEscape?: () => void;
}) => {
  if (captureKeypresses) {
    return (
      <ActivityBuilderDrawerAlly onCloseByEscape={onCloseByEscape}>
        {children}
      </ActivityBuilderDrawerAlly>
    );
  }

  return (
    <Styled.ActivityBuilderDrawerWrapper>
      {children}
    </Styled.ActivityBuilderDrawerWrapper>
  );
};

const ActivityBuilderDrawerAlly = ({
  children,
  onCloseByEscape,
}: {
  children: ReactNode;
  onCloseByEscape?: () => void;
}) => {
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  useKeyBoardTrap({
    containerRef,
    isActive: true,
    captureKeypress: true,
    close: onCloseByEscape ? onCloseByEscape : () => {},
  });

  return (
    <Styled.ActivityBuilderDrawerWrapper ref={setContainerRef}>
      {children}
    </Styled.ActivityBuilderDrawerWrapper>
  );
};

export const ActivityBuilderDrawerLoading = ({
  title,
  onClose,
}: {
  title?: string;
  onClose: () => void;
}) => (
  <ActivityBuilderDrawerWrapper>
    <ActivityBuilderDrawerHeader title={title} onClose={onClose} />
    <ActivityBuilderDrawerContent>
      <Bouncer />
    </ActivityBuilderDrawerContent>
  </ActivityBuilderDrawerWrapper>
);

export const ActivityBuilderDrawerHeaderButton = ({
  label,
  onClick,
  ariaLabel,
}: {
  label: string;
  onClick: () => void;
  ariaLabel: string;
}) => (
  <SecondaryButton
    type="button"
    aria-label={ariaLabel}
    $isSmall
    onClick={onClick}
    data-selector={`btn-${formatToLowerKebabCase(label)}`}
  >
    {label}
  </SecondaryButton>
);
